// src/contexts/useUserStore.js

import { create } from 'zustand';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { auth, firestore } from '../config/firebaseConfig';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const useUserStore = create((set) => ({
  isAuthenticated: false,
  userId: null,
  displayName: null,
  preferences: {},
  baseLocation: null,
  lastBrowsedLocation: null,
  status: null,
  isSigningIn: false,

  login: async () => {
    const provider = new GoogleAuthProvider();
    set({ isSigningIn: true });
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      set({
        isAuthenticated: true,
        userId: user.uid,
        displayName: user.displayName || "User",
      });
      await useUserStore.getState().loadPreferences();
    } catch (error) {
      console.error("An error occurred during login:", error);
    } finally {
      set({ isSigningIn: false });
    }
  },

  logout: async () => {
    await signOut(auth);
    set({
      isAuthenticated: false,
      userId: null,
      displayName: null,
      preferences: {},
      baseLocation: null,
      lastBrowsedLocation: null,
      status: null,
    });
  },

  setBaseLocation: async (location) => {
    set({ baseLocation: location });
    await useUserStore.getState().updatePreferences();
  },

  setStatus: async (status) => {
    set({ status });
    await useUserStore.getState().updatePreferences();
  },

  loadPreferences: async () => {
    const user = auth.currentUser;
    if (user) {
      const docRef = doc(firestore, 'preferences', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        set({
          preferences: data,
          baseLocation: data.baseLocation || null,
          lastBrowsedLocation: data.lastBrowsedLocation || null,
          status: data.status || null,
        });
      } else {
        const defaultPreferences = {
          baseLocation: { name: "", coords: [0, 0] },
          lastBrowsedLocation: { name: "", coords: [0, 0] },
          status: "Basic",
        };
        await setDoc(docRef, defaultPreferences);
        set({ preferences: defaultPreferences });
      }
    }
  },

  updatePreferences: async () => {
    const user = auth.currentUser;
    if (user) {
      const docRef = doc(firestore, 'preferences', user.uid);
      const { baseLocation, lastBrowsedLocation, status } = useUserStore.getState();
      await updateDoc(docRef, { baseLocation, lastBrowsedLocation, status });
    }
  },
}));

// Initialize Firebase auth state listener
onAuthStateChanged(auth, (user) => {
  if (user) {
    useUserStore.setState({
      isAuthenticated: true,
      userId: user.uid,
      displayName: user.displayName || "User",
    });
    useUserStore.getState().loadPreferences();
  } else {
    useUserStore.setState({
      isAuthenticated: false,
      userId: null,
      displayName: null,
      preferences: {},
    });
  }
});

export default useUserStore;
